import type { GameState } from "../engine/types";

const CURRENT_KEY = "kiteward.run.current";
const PREVIOUS_KEY = "kiteward.run.previous";
const SAVE_VERSION = 3;

type SaveEnvelope = {
  version: number;
  savedAt: string;
  state: GameState;
};

export function encodeRun(state: GameState): string {
  const envelope: SaveEnvelope = {
    version: SAVE_VERSION,
    savedAt: new Date().toISOString(),
    state: {
      ...state,
      events: []
    }
  };
  return JSON.stringify(envelope);
}

export function decodeRun(value: string): GameState | null {
  try {
    const parsed = JSON.parse(value) as Partial<SaveEnvelope>;
    if (parsed.version !== SAVE_VERSION || !isGameState(parsed.state)) {
      return null;
    }
    return {
      ...parsed.state,
      events: []
    };
  } catch {
    return null;
  }
}

export function saveRun(state: GameState): void {
  try {
    const current = localStorage.getItem(CURRENT_KEY);
    if (current) {
      localStorage.setItem(PREVIOUS_KEY, current);
    }
    localStorage.setItem(CURRENT_KEY, encodeRun(state));
  } catch {
    return;
  }
}

export function loadRun(): GameState | null {
  try {
    const current = localStorage.getItem(CURRENT_KEY);
    const decoded = current ? decodeRun(current) : null;
    if (decoded) {
      return decoded;
    }
    const previous = localStorage.getItem(PREVIOUS_KEY);
    return previous ? decodeRun(previous) : null;
  } catch {
    return null;
  }
}

export function clearRun(): void {
  try {
    localStorage.removeItem(CURRENT_KEY);
    localStorage.removeItem(PREVIOUS_KEY);
  } catch {
    return;
  }
}

function isGameState(value: unknown): value is GameState {
  if (!value || typeof value !== "object") {
    return false;
  }
  const candidate = value as Partial<GameState>;
  return (
    (candidate.phase === "running" || candidate.phase === "upgrade") &&
    typeof candidate.seed === "number" &&
    typeof candidate.rngState === "number" &&
    typeof candidate.time === "number" &&
    typeof candidate.wave === "number" &&
    typeof candidate.score === "number" &&
    Array.isArray(candidate.towers) &&
    Array.isArray(candidate.enemies) &&
    Array.isArray(candidate.projectiles) &&
    Array.isArray(candidate.schedule) &&
    Array.isArray(candidate.upgrades) &&
    Array.isArray(candidate.offeredUpgrades) &&
    typeof candidate.hearth?.health === "number" &&
    typeof candidate.wind?.angle === "number" &&
    typeof candidate.modifiers?.pressureCapacity === "number" &&
    typeof candidate.aim?.point?.x === "number" &&
    typeof candidate.aim?.point?.y === "number" &&
    typeof candidate.modifiers?.projectileCount === "number" &&
    typeof candidate.modifiers?.curvedDamageMultiplier === "number" &&
    typeof candidate.modifiers?.gustCost === "number" &&
    typeof candidate.modifiers?.gustReloadsTowers === "boolean" &&
    typeof candidate.statistics?.shots === "number"
  );
}
